import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import { ContextSession } from "../../services/ContextSession";
import { apiRequest } from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  formatTelefonoVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";
import injupempGobiernoLogo from "../../img/LogoInjupemp2026.png";

const UI_PRIMARY = "#19498c";

const OFICINA_NOMBRES = {
  1: "Tegucigalpa",
  2: "San Pedro Sula",
  3: "La Ceiba",
  4: "Choluteca",
  5: "Santa Rosa de Copán",
  6: "Santa Bárbara",
  7: "Juticalpa",
};

const resolverNombreOficina = (oficinaSesion) => {
  const raw = String(oficinaSesion ?? "").trim();
  if (!raw) return OFICINA_NOMBRES[1];
  return OFICINA_NOMBRES[raw] || OFICINA_NOMBRES[Number(raw)] || raw;
};

const emptyCatalogos = {
  documentoTipos: [],
  estadosCiviles: [],
  sexos: [],
  nacionalidades: [],
  nivelesEducativos: [],
  perfilesLaft: [],
  profesiones: [],
  viviendaTipos: [],
  ingresosAproximados: [],
  girosEntidad: [],
  cargos: [],
  departamentos: [],
  municipios: [],
  colonias: [],
  parentescos: [],
  tipoClientes: [],
  referenciaTipos: [],
};

const asText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const toIsoDate = (value) => {
  const raw = asText(value);
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const dmyMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
  return "";
};

const yearsSinceDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return "";
  const from = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(from.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - from.getFullYear();
  const monthDiff = now.getMonth() - from.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < from.getDate())) {
    years -= 1;
  }
  return years >= 0 ? String(years) : "";
};

const boolToSiNo = (value) => {
  if (value === true || value === 1 || value === "1") return "Si";
  if (value === false || value === 0 || value === "0") return "No";
  const normalized = asText(value).toLowerCase();
  if (["si", "sí", "yes", "true", "t"].includes(normalized)) return "Si";
  if (["no", "false", "f"].includes(normalized)) return "No";
  return "No";
};

const getCatalogLabel = (options, valueKey, labelKey, value) => {
  const id = asText(value);
  if (!id) return "";
  const match = (options || []).find(
    (item) => asText(item?.[valueKey]).toUpperCase() === id.toUpperCase(),
  );
  if (match) return asText(match?.[labelKey]);
  return "";
};

const resolveCatalogText = (options, valueKey, labelKey, rawValue) => {
  const raw = asText(rawValue);
  if (!raw) return "";
  const fromId = getCatalogLabel(options, valueKey, labelKey, raw);
  if (fromId) return fromId;
  const matchByLabel = (options || []).find(
    (item) => asText(item?.[labelKey]).toUpperCase() === raw.toUpperCase(),
  );
  if (matchByLabel) return asText(matchByLabel?.[labelKey]);
  return raw;
};

const resolveGenero = (value) => {
  const raw = asText(value).toUpperCase();
  if (!raw) return "";
  if (raw === "M" || raw.includes("MASC")) return "M";
  if (raw === "F" || raw.includes("FEM")) return "F";
  return raw;
};

const normalizeReferenciaTipo = (value) => {
  const text = asText(value).toUpperCase();
  if (!text) return "";
  if (text.includes("COMERCIAL") || text.includes("BANC"))
    return "COMERCIALES / BANCARIAS";
  if (text.includes("LABOR")) return "LABORALES";
  if (text.includes("PERSON")) return "PERSONALES";
  return text;
};

const mapPayloadToFicha = (payload, catalogos) => {
  const persona = payload?.persona || {};
  const info = payload?.informacion_adicional || {};

  const form = {
    tipoDocumento:
      asText(persona?.DocumentoTipoDescripcion) ||
      resolveCatalogText(
        catalogos.documentoTipos,
        "DocumentoTipoId",
        "Descripcion",
        persona?.FKDocumentoTipoId,
      ),
    identificacion: asText(persona?.DocumentoNumero || persona?.PersonaId),
    primerNombre: asText(persona?.Nombre1),
    segundoNombre: asText(persona?.Nombre2),
    primerApellido: asText(persona?.Apellido1),
    segundoApellido: asText(persona?.Apellido2),
    genero: resolveGenero(persona?.FKSexoId || persona?.SexoDescripcion),
    paisNacimiento:
      asText(persona?.PaisNacimientoDescripcion) ||
      resolveCatalogText(
        catalogos.nacionalidades,
        "NacionalidadId",
        "Descripcion",
        persona?.FKPaisNacimientoId,
      ),
    nacionalidad:
      asText(persona?.NacionalidadDescripcion) ||
      resolveCatalogText(
        catalogos.nacionalidades,
        "NacionalidadId",
        "Descripcion",
        persona?.FKNacionalidadId,
      ),
    fechaNacimiento: toIsoDate(persona?.FechaNacimiento),
    lugarNacimiento: asText(info?.LugarNacimiento),
    estadoCivil:
      asText(persona?.EstadoCivilDescripcion) ||
      resolveCatalogText(
        catalogos.estadosCiviles,
        "EstadoCivilId",
        "Descripcion",
        persona?.FKEstadoCivilId,
      ),
    departamentoResidencia: resolveCatalogText(
      catalogos.departamentos,
      "DepartamentoId",
      "Nombre",
      persona?.FKDepartamentoId,
    ),
    municipioResidencia: resolveCatalogText(
      catalogos.municipios,
      "MunicipioId",
      "Nombre",
      persona?.FKMunicipioId,
    ),
    coloniaResidencia: resolveCatalogText(
      catalogos.colonias,
      "ColoniaId",
      "Nombre",
      persona?.FKColoniaId,
    ),
    direccionCompleta: asText(persona?.Direccion),
    calle: asText(info?.Calle),
    numeroCasa: asText(info?.NumeroCasa),
    referenciaDireccion: asText(info?.ReferenciaDomicilio),
    tiempoResidir: yearsSinceDate(info?.TiempoResidirUbicacion),
    fechaDesdeReside: toIsoDate(info?.TiempoResidirUbicacion),
    telefono: asText(persona?.Telefonos || persona?.Telefono),
    movil: asText(persona?.Movil),
    correo: asText(persona?.Correo),
    tipoCliente: resolveCatalogText(
      catalogos.tipoClientes,
      "TipoClienteSolicitanteId",
      "Descripcion",
      info?.FKTipoClienteSolicitanteId,
    ),
    rtn: asText(info?.RTN),
    profesion: resolveCatalogText(
      catalogos.profesiones,
      "ProfesionOficioId",
      "Descripcion",
      persona?.FKProfesionOficioId || info?.FKProfesionOficioId,
    ),
    nivelEducativo: resolveCatalogText(
      catalogos.nivelesEducativos,
      "NivelEducativoId",
      "Descripcion",
      persona?.FKNivelEducativoId || info?.FKNivelEducativoId,
    ),
    nombreConyuge: asText(info?.ConyugeNombre),
    tipoDocumentoConyuge: resolveCatalogText(
      catalogos.documentoTipos,
      "DocumentoTipoId",
      "Descripcion",
      info?.FKConyugeDocumentoTipoId,
    ),
    identificacionConyuge: asText(info?.ConyugeDocumentoNumero),
    generoConyuge: resolveGenero(info?.FKConyugeSexoId),
    fechaNacimientoConyuge: toIsoDate(info?.ConyugeFechaNacimiento),
    patrono: asText(info?.EmpleadoPatrono),
    departamentoTrabajo: resolveCatalogText(
      catalogos.departamentos,
      "DepartamentoId",
      "Nombre",
      persona?.FKDepartamentoTrabajoId || info?.FKDepartamentoTrabajoId,
    ),
    municipioTrabajo: resolveCatalogText(
      catalogos.municipios,
      "MunicipioId",
      "Nombre",
      persona?.FKMunicipioTrabajoId || info?.FKMunicipioTrabajoId,
    ),
    coloniaTrabajo: resolveCatalogText(
      catalogos.colonias,
      "ColoniaId",
      "Nombre",
      persona?.FKColoniaTrabajoId || info?.FKColoniaTrabajoId,
    ),
    direccionTrabajo: asText(
      persona?.DireccionTrabajo || info?.DireccionTrabajo,
    ),
    numeroCasaTrabajo: asText(info?.NumeroCasaTrabajo),
    referenciaTrabajo: asText(info?.RefrerenciaUbicacionTrabajo),
    fechaIngresoInstitucion: toIsoDate(info?.FechaIngresoInstitucion),
    antiguedadLaboral: asText(info?.AntiguedadLaboral),
    ingresoAproxRango: resolveCatalogText(
      catalogos.ingresosAproximados,
      "IngresosAproximadosId",
      "Ingresos",
      info?.FKIngresosAproximadosId,
    ),
    ingresoAproxMonto: asText(info?.IngresosMensuales),
    giroEntidad: resolveCatalogText(
      catalogos.girosEntidad,
      "GiroEntidadId",
      "Descripcion",
      info?.FKGiroEntidadId,
    ),
    cargoDesempenado: resolveCatalogText(
      catalogos.cargos,
      "CargoId",
      "Descripcion",
      info?.FKCargoId,
    ),
    areaTrabajo: asText(persona?.AreaTrabajo || info?.AreaTrabajo),
    telefonoTrabajo: asText(persona?.TelefonoTrabajo || info?.TelefonoTrabajo),
    tipoVivienda: resolveCatalogText(
      catalogos.viviendaTipos,
      "ViviendaTipoId",
      "Descripcion",
      info?.FKViviendaTipoId,
    ),
    poseeVehiculo: boolToSiNo(info?.PoseeVihiculo),
    numeroVehiculos: asText(info?.NumeroVehiculo),
    poseeInmuebles: boolToSiNo(info?.OtrosInmuebles),
    descOtrosInmuebles: asText(info?.DescripcionOtrosInmuebles),
    otrosIngresos: boolToSiNo(info?.OtrosIngresos),
    descOtrosIngresos: asText(info?.DescripcionOtrosIngresos),
    montoOtrosIngresos: asText(info?.MontoOtrosIngresos),
    dependientesEconomicos: asText(info?.DependientesEconomicos),
    esPep: Boolean(persona?.PoliticamenteExpuesta),
    enviarRevision: false,
    noDesignacionBeneficiarios: false,
    perfilLaft: resolveCatalogText(
      catalogos.perfilesLaft,
      "PerfilLAFTId",
      "Descripcion",
      info?.FKPerfilLAFTId,
    ),
  };

  const referencias = (payload?.referencias || []).map((item, index) => ({
    id: item?.ReferenciaId || `ref-${index}`,
    nombreCompleto: asText(item?.Nombre),
    direccion: asText(item?.Direccion),
    telefono: asText(item?.Telefono),
    tipoReferencia: normalizeReferenciaTipo(
      resolveCatalogText(
        catalogos.referenciaTipos,
        "ReferenciaId",
        "Nombre",
        item?.FKReferenciaTipoId || item?.TipoReferencia,
      ),
    ),
  }));

  const beneficiarios = (payload?.beneficiarios || []).map((item, index) => ({
    id: item?.BeneficiarioId || `benef-${index}`,
    primerNombre: asText(item?.Nombre1),
    segundoNombre: asText(item?.Nombre2),
    primerApellido: asText(item?.Apellido1),
    segundoApellido: asText(item?.Apellido2),
    numeroDocumento: asText(item?.DocumentoNumero),
    parentesco:
      resolveCatalogText(
        catalogos.parentescos,
        "ParentescoId",
        "Descripcion",
        item?.FKParentescoId,
      ) || asText(item?.FKParentescoId),
  }));

  const otrosCargos = (payload?.cargos_publicos || []).map((item, index) => ({
    id: item?.CargoPublicoDesempenadoId || `cargo-${index}`,
    empleadorPatrono: asText(item?.EmpleadoPatrono),
    cargoDesempenado:
      resolveCatalogText(
        catalogos.cargos,
        "CargoId",
        "Descripcion",
        item?.FKCargoDesempenadoId,
      ) || asText(item?.FKCargoDesempenadoId),
    actividadGiro:
      resolveCatalogText(
        catalogos.girosEntidad,
        "GiroEntidadId",
        "Descripcion",
        item?.FKGiroEntidadId,
      ) || asText(item?.FKGiroEntidadId),
  }));

  const ppeFamiliares = (
    payload?.ppe_familiares ||
    payload?.familiares_ppe ||
    payload?.persona_politicamente_expuesta_familiares ||
    []
  ).map((item, index) => ({
    id: item?.FamiliarPpeId || `fam-${index}`,
    primerNombre: asText(item?.Nombre1),
    segundoNombre: asText(item?.Nombre2),
    primerApellido: asText(item?.Apellido1),
    segundoApellido: asText(item?.Apellido2),
    parentesco: asText(item?.Parentesco || item?.Vinculo || item?.parentesco),
    nacionalidad: asText(item?.Nacionalidad || item?.nacionalidad),
  }));

  return {
    personaId: asText(persona?.PersonaId),
    form,
    referencias,
    beneficiarios,
    otrosCargos,
    ppeFamiliares,
  };
};

export default function ImprimirFichaAfiliacion() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const [catalogos, setCatalogos] = useState({ ...emptyCatalogos });
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [dni, setDni] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [fichaData, setFichaData] = useState(null);

  useEffect(() => {
    let active = true;
    const loadCatalogos = async () => {
      if (!authToken) return;
      try {
        setLoadingCatalogos(true);
        const data = await apiRequest("/Enrolamiento/catalogos/", {
          authToken,
        });
        if (!active) return;
        const c = data?.catalogos || {};
        setCatalogos({
          documentoTipos: c?.documento_tipos || [],
          estadosCiviles: c?.estados_civiles || [],
          sexos: c?.sexos || [],
          nacionalidades: c?.nacionalidades || [],
          nivelesEducativos: c?.niveles_educativos || [],
          perfilesLaft: c?.perfiles_laft || [],
          profesiones: c?.profesiones || [],
          viviendaTipos: c?.vivienda_tipos || [],
          ingresosAproximados: c?.ingresos_aproximados || [],
          girosEntidad: c?.giros_entidad || [],
          cargos: c?.cargos || [],
          departamentos: c?.departamentos || [],
          municipios: c?.municipios || [],
          colonias: c?.colonias || [],
          parentescos: c?.parentescos || [],
          tipoClientes: c?.tipo_cliente_solicitantes || [],
          referenciaTipos: c?.referencia_tipos || [],
        });
      } catch (_err) {
        if (!active) return;
        setCatalogos({ ...emptyCatalogos });
      } finally {
        if (active) setLoadingCatalogos(false);
      }
    };

    loadCatalogos();
    return () => {
      active = false;
    };
  }, [authToken]);

  const nombreEncontrado = useMemo(() => {
    if (!fichaData?.form) return "";
    return [
      fichaData.form.primerNombre,
      fichaData.form.segundoNombre,
      fichaData.form.primerApellido,
      fichaData.form.segundoApellido,
    ]
      .filter(Boolean)
      .join(" ");
  }, [fichaData]);

  const handleBuscar = async () => {
    const cleanedDni = asText(dni);
    if (!cleanedDni) {
      setError("Ingrese el DNI para realizar la búsqueda.");
      setOk("");
      setFichaData(null);
      return;
    }

    try {
      setSearching(true);
      setError("");
      setOk("");
      setFichaData(null);

      const searchData = await apiRequest(
        `/Enrolamiento/personas/buscar/?dni=${encodeURIComponent(cleanedDni)}&beneficiario_tipo=P`,
        { authToken },
      );

      const personaId =
        asText(searchData?.persona?.PersonaId) ||
        asText(searchData?.persona?.personaId) ||
        asText(searchData?.resultados?.[0]?.PersonaId);

      if (!personaId) {
        setError("Persona no encontrada.");
        return;
      }

      const detalle = await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(personaId)}/?beneficiario_tipo=P`,
        { authToken },
      );

      const mapped = mapPayloadToFicha(detalle, catalogos);
      setFichaData(mapped);
      setOk("Persona encontrada. Puede visualizar la ficha.");
    } catch (err) {
      setError(err?.message || "Persona no encontrada.");
      setOk("");
      setFichaData(null);
    } finally {
      setSearching(false);
    }
  };

  const handleLimpiar = () => {
    setDni("");
    setError("");
    setOk("");
    setFichaData(null);
  };

  const handleOpenFichaPreview = () => {
    if (!fichaData?.form) return;

    const { form, referencias, beneficiarios, otrosCargos, ppeFamiliares } =
      fichaData;

    const toSafe = (value) =>
      `${value ?? ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const formatDate = (value) => {
      if (!value) return "";
      if (value.includes("-") && value.length >= 10) {
        const [yyyy, mm, dd] = value.split("-");
        return `${dd}-${mm}-${yyyy}`;
      }
      return value;
    };

    const generoLabel =
      form.genero === "M" ? "MASCULINO" : form.genero === "F" ? "FEMENINO" : "";
    const noCorreo = !asText(form.correo);
    const tipoViviendaUpper = asText(form.tipoVivienda).toUpperCase();

    const laborales = referencias.filter(
      (item) => item.tipoReferencia === "LABORALES",
    );
    const personales = referencias.filter(
      (item) => item.tipoReferencia === "PERSONALES",
    );
    const bancarias = referencias.filter(
      (item) => item.tipoReferencia === "COMERCIALES / BANCARIAS",
    );
    const refsRows = (list) =>
      (list.length ? list : [{}]).map(
        (item) => `
        <tr>
          <td>${toSafe(item.nombreCompleto)}</td>
          <td>${toSafe(item.direccion)}</td>
          <td>${toSafe(formatTelefonoVisual(item.telefono))}</td>
        </tr>
      `,
      );

    const beneficiariosRows = (beneficiarios.length ? beneficiarios : [{}]).map(
      (item, index) => `
        <tr>
          <td class="cell-center">${item.id ? index + 1 : ""}</td>
          <td>${toSafe([item.primerNombre, item.segundoNombre].filter(Boolean).join(" "))}</td>
          <td>${toSafe([item.primerApellido, item.segundoApellido].filter(Boolean).join(" "))}</td>
          <td>${toSafe(formatIdentificacionVisual(item.numeroDocumento))}</td>
          <td>${toSafe(item.parentesco)}</td>
        </tr>
      `,
    );

    const cargosRows = (otrosCargos.length ? otrosCargos : [{}]).map(
      (item, index) => `
        <tr>
          <td>${item.id ? index + 1 : ""}</td>
          <td>${toSafe(item.empleadorPatrono)}</td>
          <td>${toSafe(item.cargoDesempenado)}</td>
          <td>${toSafe(item.actividadGiro)}</td>
        </tr>
      `,
    );

    const nombreOficinaFirma = toSafe(
      resolverNombreOficina(sessi?.sessionData?.oficina),
    );
    const _now = new Date();
    const MESES = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    const fechaLarga = `${_now.getDate()} días de ${MESES[_now.getMonth()]} de ${_now.getFullYear()}`;
    const horaLarga = _now.toLocaleTimeString("es-HN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const html = `
      <!doctype html>
      <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Ficha de Afiliacion</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; margin: 0; background: #AD8401; color: #111; font-size: 11px; }
          .toolbar { position: sticky; top: 0; z-index: 10; background: #fff; padding: 10px 14px; border-bottom: 1px solid #ccc; display: flex; gap: 8px; }
          .toolbar button { padding: 8px 14px; border: 1px solid #888; background: #fff; cursor: pointer; font-weight: 700; }
          .page { width: 216mm; margin: 8px auto; background: #fff; padding: 6mm; box-sizing: border-box; }
          .page + .page { margin-top: 4mm; }
          .page-break { page-break-after: always; break-after: page; }
          .header { display: grid; grid-template-columns: 50% 50%; align-items: center; border: 1px solid #111; border-bottom: none; }
          .header img { height: 64px; max-width: 100%; object-fit: contain; margin: 6px 8px; }
          .title { text-align: center; font-size: 22px; font-weight: 700; }
          .section-title { margin: 4px 0 0; padding: 5px 7px; background: #efefef; border: 1px solid #111; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
          th, td { border: 1px solid #111; padding: 3px 5px; vertical-align: top; word-wrap: break-word; line-height: 1.25; }
          th { background: #f6f6f6; text-align: left; font-weight: 700; }
          .linea-firma { border-top: 1px solid #111; margin-top: 24px; padding-top: 4px; text-align: center; font-size: 10px; }
          .firma { margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .cell-center { text-align: center; }
          .legal-title { margin: 6px 0 0; padding: 5px; background: #dedede; border: 1px solid #111; font-size: 12px; font-weight: 700; text-align: center; text-transform: uppercase; }
          .legal-box { border: 1px solid #111; border-top: none; padding: 6px; font-size: 11px; line-height: 1.3; }
          .mini-row { display: grid; grid-template-columns: 86px 1fr; border: 1px solid #111; border-top: none; }
          .mini-row .left { border-right: 1px solid #111; font-size: 10px; font-weight: 700; padding: 4px; text-align: center; text-transform: uppercase; line-height: 1.15; word-break: normal; }
          .mini-row .right { padding: 5px 7px; font-size: 11px; line-height: 1.3; }
          .check-line { display: flex; align-items: center; gap: 20px; padding: 5px 7px; font-size: 11px; }
          .check { display: inline-block; width: 11px; height: 11px; border: 1px solid #111; margin-right: 4px; vertical-align: middle; }
          .fill-line { display: inline-block; border-bottom: 1px solid #111; min-width: 80px; height: 12px; vertical-align: middle; }
          .footer-signatures { margin-top: 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; }
          .footer-signatures .line { border-top: 1px solid #111; padding-top: 3px; text-align: center; font-size: 10px; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          table { page-break-inside: auto; break-inside: auto; }
          .compact-section { margin-top: 6px; }
          @page { size: legal portrait; margin: 8mm; }
          @media print {
            body { background: #fff; }
            .toolbar { display: none; }
            .page { margin: 0; width: auto; min-height: 0; padding: 5mm 3mm; }
            .page + .page { margin-top: 0; }
            .page-break { page-break-after: auto; break-after: auto; }
            .second-page { page-break-before: always; break-before: page; }
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button onclick="window.print()">Imprimir</button>
          <button onclick="window.close()">Cerrar</button>
        </div>
        <div class="page">
          <div class="header">
            <div><img src="${injupempGobiernoLogo}" alt="Logo" /></div>
            <div class="title">FORMULARIO DE AFILIACIÓN</div>
          </div>

          <div class="section-title">I. DATOS DEL SOLICITANTE</div>
          <table>
            <tr><th>Primer Nombre</th><th>Segundo Nombre</th><th>Primer Apellido</th><th>Segundo Apellido</th></tr>
            <tr><td>${toSafe(form.primerNombre)}</td><td>${toSafe(form.segundoNombre)}</td><td>${toSafe(form.primerApellido)}</td><td>${toSafe(form.segundoApellido)}</td></tr>
            <tr><th>Tipo de Identificación</th><th>N° de Documento de Identificación</th><th>Fecha de Nacimiento</th><th></th></tr>
            <tr><td>${toSafe(form.tipoDocumento)}</td><td>${toSafe(formatIdentificacionVisual(form.identificacion))}</td><td>${toSafe(formatDate(form.fechaNacimiento))}</td><td></td></tr>
            <tr><th>Lugar de Nacimiento</th><th>Género</th><th>Nacionalidad</th><th>Estado Civil</th></tr>
            <tr><td>${toSafe(form.lugarNacimiento)}</td><td>${toSafe(generoLabel)}</td><td>${toSafe(form.nacionalidad)}</td><td>${toSafe(form.estadoCivil)}</td></tr>
            <tr><th>Profesión u Oficio / Actividad Económica</th><th>Número de Teléfono Fijo</th><th>Número de Teléfono Celular</th><th></th></tr>
            <tr><td>${toSafe(form.profesion)}</td><td>${toSafe(formatTelefonoVisual(form.telefono))}</td><td>${toSafe(formatTelefonoVisual(form.movil))}</td><td></td></tr>
            <tr><th>Correo Electrónico</th><th>Nombre del Conyuge</th><th></th><th></th></tr>
            <tr><td>${toSafe(noCorreo ? "" : form.correo)}</td><td>${toSafe(form.nombreConyuge)}</td><td></td><td></td></tr>
          </table>

          <div class="section-title compact-section">II. INFORMACIÓN RESIDENCIAL</div>
          <table>
            <tr><th>Barrio o Colonia</th><th>Calle o Avenida</th><th>N° de Casa</th><th>Municipio</th></tr>
            <tr><td>${toSafe(form.coloniaResidencia)}</td><td>${toSafe(form.calle)}</td><td>${toSafe(form.numeroCasa)}</td><td>${toSafe(form.municipioResidencia)}</td></tr>
            <tr><th>Departamento</th><th>Tiempo de Residir</th><th colspan="2">Referencia de Domicilio</th></tr>
            <tr><td>${toSafe(form.departamentoResidencia)}</td><td>${toSafe(form.tiempoResidir)}</td><td colspan="2">${toSafe(form.referenciaDireccion)}</td></tr>
          </table>

          <div class="section-title compact-section">III. INFORMACIÓN LABORAL</div>
          <table>
            <tr><th>Empleador / Patrono</th><th>Barrio o Colonia</th><th>N° de Casa</th><th>Municipio</th></tr>
            <tr><td>${toSafe(form.patrono)}</td><td>${toSafe(form.coloniaTrabajo)}</td><td>${toSafe(form.numeroCasaTrabajo)}</td><td>${toSafe(form.municipioTrabajo)}</td></tr>
            <tr><th>Departamento</th><th>Número de Teléfono</th><th>Referencia de Ubicación</th><th>Giro de Entidad</th></tr>
            <tr><td>${toSafe(form.departamentoTrabajo)}</td><td>${toSafe(formatTelefonoVisual(form.telefonoTrabajo))}</td><td>${toSafe(form.referenciaTrabajo)}</td><td>${toSafe(form.giroEntidad)}</td></tr>
            <tr><th>Cargo desempeñado</th><th>Antigüedad Laboral</th><th>Ingresos Aproximados (miles de L.)</th><th>Ingresos Aproximados (monto)</th></tr>
            <tr><td>${toSafe(form.cargoDesempenado)}</td><td>${toSafe(form.antiguedadLaboral)}</td><td>${toSafe(form.ingresoAproxRango)}</td><td>${toSafe(form.ingresoAproxMonto)}</td></tr>
          </table>

          <div class="section-title compact-section">OTROS CARGOS PUBLICOS DESEMPEÑADOS EN LOS ULTIMOS CUATRO (4) AÑOS</div>
          <table>
            <tr><th style="width:7%;">No</th><th>Empleador / Patrono</th><th>Cargo Desempeñado</th><th>Actividad o Giro de la Empresa</th></tr>
            ${cargosRows.join("")}
          </table>

          <div class="section-title compact-section">IV. PATRIMONIO</div>
          <table>
            <tr>
              <th>Vivienda</th>
              <td>Propia ${tipoViviendaUpper.includes("PROPIA") ? "X" : ""}</td>
              <td>Alquilada ${tipoViviendaUpper.includes("ALQUIL") ? "X" : ""}</td>
              <td>Familiar ${tipoViviendaUpper.includes("FAMILIAR") ? "X" : ""}</td>
              <td>Hipoteca ${tipoViviendaUpper.includes("HIPOT") ? "X" : ""}</td>
            </tr>
            <tr>
              <th>Posee Vehículo</th>
              <td>Si ${form.poseeVehiculo === "Si" ? "X" : ""}</td>
              <td>No ${form.poseeVehiculo === "No" ? "X" : ""}</td>
              <th>Número de Vehículos</th>
              <td>${form.poseeVehiculo === "Si" ? toSafe(form.numeroVehiculos) : ""}</td>
            </tr>
            <tr>
              <th>Otros Inmuebles</th>
              <td>Si ${form.poseeInmuebles === "Si" ? "X" : ""}</td>
              <td>No ${form.poseeInmuebles === "No" ? "X" : ""}</td>
              <th>Describa</th>
              <td>${form.poseeInmuebles === "Si" ? toSafe(form.descOtrosInmuebles) : ""}</td>
            </tr>
            <tr>
              <th>Otros Ingresos</th>
              <td>Si ${form.otrosIngresos === "Si" ? "X" : ""}</td>
              <td>No ${form.otrosIngresos === "No" ? "X" : ""}</td>
              <th>Origen de Otros Ingresos</th>
              <td>${form.otrosIngresos === "Si" ? toSafe(form.descOtrosIngresos) : ""}</td>
            </tr>
            <tr>
              <th colspan="3">Monto Aproximado de Otros Ingresos (mensuales)</th>
              <td colspan="2">${form.otrosIngresos === "Si" ? toSafe(form.montoOtrosIngresos) : ""}</td>
            </tr>
          </table>

          <div class="section-title compact-section">V. REFERENCIAS LABORALES</div>
          <table>
            <tr><th>Nombre Completo</th><th>Dirección</th><th>N° de Teléfonos</th></tr>
            ${refsRows(laborales).join("")}
          </table>

          <div class="section-title compact-section">VI. REFERENCIAS PERSONALES</div>
          <table>
            <tr><th>Nombre Completo</th><th>Dirección</th><th>N° de Teléfonos</th></tr>
            ${refsRows(personales).join("")}
          </table>

          <div class="section-title compact-section">VII. REFERENCIAS BANCARIAS</div>
          <table>
            <tr><th>Institución Bancaria o Comercial</th><th>N° de Teléfonos</th></tr>
            ${(bancarias.length ? bancarias : [{}])
              .map(
                (item) => `
              <tr>
                <td>${toSafe(item.nombreCompleto)}</td>
                <td>${toSafe(formatTelefonoVisual(item.telefono))}</td>
              </tr>
            `,
              )
              .join("")}
          </table>

        </div>

        <div class="page second-page">
          <div class="section-title">VIII. BENEFICIARIOS</div>
          <table>
            <tr><th style="width:7%;">No</th><th>Nombres</th><th>Apellidos</th><th>N° de Documento</th><th>Parentesco</th></tr>
            ${beneficiariosRows.join("")}
          </table>

          

          <div class="legal-title">IX. Persona Expuesta Politicamente (PEP)</div>
          <div class="legal-box">
            <b>DEFINICION:</b> Las Personas Expuestas Politicamente son aquellas que desempeñan o han desempeñado
            funciones publicas destacadas en un pais extranjero o en su propio pais, por ejemplo jefes de Estado, o de
            un gobierno, politicos de alta jerarquia, funcionarios gubernamentales, judiciales o militares de alta
            jerarquia, altos ejecutivos de empresas estatales, funcionarios importantes de partidos politicos. Ej.
            Presidentes de Estado, Titulares de Poderes de Estado e Instituciones Publicas, Alcaldes, Vicealcaldes,
            Regidores, Diputados, Magistrados de la Corte Suprema de Justicia, Miembros Tribunal de Estado Mayor
            Conjunto, entre otros.
          </div>
          <table style="margin-top:0; border-top:none;">
            <tr>
              <td style="width:70%; border-top:none;"><b>¿Es usted una Persona Expuesta Politicamente?</b></td>
              <td style="width:15%; text-align:center; border-top:none;"><span class="check">${form.esPep ? "X" : ""}</span>&nbsp;Si</td>
              <td style="width:15%; text-align:center; border-top:none;"><span class="check">${!form.esPep ? "X" : ""}</span>&nbsp;No</td>
            </tr>
          </table>
          ${
            form.esPep
              ? `
          <div class="section-title compact-section" style="font-size:11px; margin-top:4px;">
            Nombre de familiares hasta segundo grado de consanguinidad y afinidad
          </div>
          <table>
            <tr>
              <th>Primer Nombre</th><th>Segundo Nombre</th><th>Primer Apellido</th>
              <th>Segundo Apellido</th><th>Vínculo/Parentesco</th><th>Nacionalidad</th>
            </tr>
            ${(ppeFamiliares.length ? ppeFamiliares : [{}])
              .map(
                (f) => `
            <tr>
              <td>${toSafe(f.primerNombre)}</td>
              <td>${toSafe(f.segundoNombre)}</td>
              <td>${toSafe(f.primerApellido)}</td>
              <td>${toSafe(f.segundoApellido)}</td>
              <td>${toSafe(f.parentesco)}</td>
              <td>${toSafe(f.nacionalidad)}</td>
            </tr>`,
              )
              .join("")}
          </table>`
              : ""
          }
          <div class="mini-row" style="margin-top:4px;">
            <div class="left">Proposito / uso de la cuenta:</div>
            <div class="right">Ingreso de mis Cotizaciones y Aportaciones</div>
          </div>

          <div class="mini-row">
            <div class="left">X. Declaración</div>
            <div class="right">
              Asi mismo, hago constar que:
              <br />
              1. La informacion detallada en la presente solicitud es verdadera y los documentos adjuntos han sido
              extendidos por autoridad competente.
              <br />
              2. Autorizo al INJUPEMP a verificar los datos e informacion declarada en la presente solicitud.
            </div>
          </div>

          <div class="mini-row">
            <div class="left">XI. Cumplimiento de requisitos</div>
            <div class="right">
              Acompaño a esta solicitud con la documentacion siguiente:
              <br />
              a) Acuerdos de Nombramiento firmada y sellada por las Oficinas de Recursos Humanos
              <span style="float:right"><span class="check"></span></span>
              <br />
              b) Una(1) fotocopia legible de la Tarjeta de Identidad
              <span style="float:right"><span class="check"></span></span>
              <br />
              c) Otros documentos acreditados: detalle:
              <span class="fill-line" style="min-width:260px;"></span>
              <span style="float:right"><span class="check"></span></span>
            </div>
          </div>

          <div class="legal-box" style="margin-top:8px;">
            <b>FUNDAMENTOS DE DERECHO:</b> Fundamento mi petición en el Articulo 80 de la Constitución de la Republica,
            Articulo 2, numeral 1 y 4 de la Ley de INJUPEMP.
          </div>
          <div class="legal-box" style="margin-top:6px;">
            <b>NOTIFICACION:</b>
            <br />
            - Esta solicitud deberá ser presentada personalmente
            <br />
            - Para efectos de notificación, el INJUPEMP utilizará el correo electrónico designado en la presente solicitud.
          </div>
          <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, hora ${horaLarga}.</p>

          <div style="margin-top:100px; text-align:center;">
            <div style="width:320px; margin:0 auto; border-top:1px solid #111; padding-top:4px; text-align:center; font-weight:700; font-size:11px;">
              FIRMA Y HUELLA DEL SOLICITANTE
            </div>
          </div>

          <div class="legal-title" style="margin-top:16px;">XII. Para Uso Exclusivo del INJUPEMP</div>
          <div style="padding:6px 0 0; font-size:10px;">
            <b>Estatus del Participante:</b>
            <span style="margin-left:24px;"><span class="check"></span>Activo</span>
            <span style="margin-left:24px;"><span class="check"></span>Voluntario</span>
            <span style="margin-left:24px;"><span class="check"></span>Suspenso</span>
          </div>
          <br><br><br><br><br><br><br>

          <div class="footer-signatures">
            <div class="line">Nombre del Auxiliar de Registro y Afiliación</div>
            <div class="line">Firma</div>
          </div>
        </div>
      </body>
      </html>
    `;

    setError("");

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const triggerPrint = () => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        cleanup();
        setError("No se pudo iniciar la impresión.");
        return;
      }

      const handleAfterPrint = () => {
        frameWindow.removeEventListener("afterprint", handleAfterPrint);
        cleanup();
      };

      frameWindow.addEventListener("afterprint", handleAfterPrint);
      frameWindow.focus();
      frameWindow.print();
      setTimeout(cleanup, 120000);
    };

    iframe.addEventListener(
      "load",
      () => {
        setTimeout(triggerPrint, 150);
      },
      { once: true },
    );

    document.body.appendChild(iframe);
    iframe.srcdoc = html;
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.4 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.08) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{
          p: { xs: 1.6, md: 2.2 },
          border: "1px solid #dbe6f6",
          borderRadius: 3,
          boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
          Imprimir Ficha de Afiliación
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto auto" },
            gap: 1.2,
            alignItems: "end",
          }}
        >
          <TextField
            label="DNI"
            size="small"
            value={formatIdentificacionVisual(dni)}
            onChange={(event) =>
              setDni(stripIdentificacionVisual(event.target.value))
            }
            inputProps={{ maxLength: 15 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            disabled={searching || loadingCatalogos}
            sx={{
              backgroundColor: UI_PRIMARY,
              color: "#fff",
              borderRadius: 2.5,
              px: 2.2,
            }}
          >
            {searching ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleLimpiar}
            sx={{
              borderColor: UI_PRIMARY,
              color: UI_PRIMARY,
              borderRadius: 2.5,
            }}
          >
            Limpiar
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mt: 1.2 }}>
            {error}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 1.2 }}>
            {ok}
          </Alert>
        )}
        {loadingCatalogos && (
          <Typography sx={{ mt: 1, color: "#4f617c", fontSize: 13 }}>
            Cargando catálogos para resolver descripciones...
          </Typography>
        )}
      </Paper>

      {fichaData && (
        <Paper
          sx={{
            p: { xs: 1.6, md: 2.2 },
            mt: 1.8,
            border: "1px solid #dbe6f6",
            borderRadius: 3,
            boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#0f3e67" }}>
                {nombreEncontrado || "Afiliado"}
              </Typography>
              <Typography sx={{ color: "#4b5f82", fontSize: 13 }}>
                DNI:{" "}
                {formatIdentificacionVisual(fichaData?.form?.identificacion) ||
                  "-"}
              </Typography>
            </Box>
            <Button
              startIcon={<PrintIcon />}
              variant="outlined"
              onClick={handleOpenFichaPreview}
              sx={{
                borderColor: UI_PRIMARY,
                color: UI_PRIMARY,
                borderRadius: 1.5,
              }}
            >
              Visualizar / Imprimir
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
